import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface AdminTokenStats {
  totalTokensSold: number;
  totalTokensUsed: number;
  totalRevenue: number;
  activeUsers: number;
  averageBalance: number;
}

export interface AdminTokenPackage {
  id: string;
  name: string;
  tokens: number;
  price: number;
  description: string;
  popular: boolean;
  savings: number;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface AdminAIService {
  id: string;
  name: string;
  cost: number;
  description: string;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface AdminTokenTransaction {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  amount: number;
  type: 'purchase' | 'usage' | 'refund' | 'bonus';
  description: string;
  referenceId?: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  createdAt: number;
  completedAt?: number;
}

export const tokenAdminService = {
  async getStats(): Promise<AdminTokenStats> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/tokens/admin/stats`);
    return response.data;
  },

  async getPackages(): Promise<AdminTokenPackage[]> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/tokens/admin/packages`);
    return response.data || [];
  },

  async createPackage(
    data: Omit<AdminTokenPackage, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<AdminTokenPackage> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/tokens/admin/packages`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async updatePackage(
    id: string,
    data: Partial<AdminTokenPackage>
  ): Promise<AdminTokenPackage> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/tokens/admin/packages/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  },

  async deletePackage(id: string): Promise<void> {
    await authService.fetchWithAuth(`${API_URL}/api/tokens/admin/packages/${id}`, {
      method: 'DELETE',
    });
  },

  async getAIServices(): Promise<AdminAIService[]> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/tokens/admin/ai-services`);
    return response.data || [];
  },

  async createAIService(
    data: Omit<AdminAIService, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<AdminAIService> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/tokens/admin/ai-services`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  },

  async updateAIService(
    id: string,
    data: Partial<AdminAIService>
  ): Promise<AdminAIService> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/tokens/admin/ai-services/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  },

  async deleteAIService(id: string): Promise<void> {
    await authService.fetchWithAuth(`${API_URL}/api/tokens/admin/ai-services/${id}`, {
      method: 'DELETE',
    });
  },

  async getTransactions(filters?: {
    type?: string;
    status?: string;
    limit?: number;
  }): Promise<AdminTokenTransaction[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/tokens/admin/transactions?${params.toString()}`
    );
    return response.data || [];
  },
};